import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import {
    userInfoRowContainerId,
    userInfoCardId,
    idConfirmationStatus,
    apiPath,
    confirmStepOutcome,
} from '../../../../constants';
import CardTemplate from '../../../shared/CardTemplate';
import LoadingSpinner from '../../../shared/LoadingSpinner';
import { SmallAccentButton, TableCellWrapper } from '../../../../styles/common';
import UsersIdConfirmation from '../../../elementLibrary/UsersTable/UserIdConfirmation';
import UserIdVerificationDelete from '../../../elementLibrary/UsersTable/UserIdVerificationDelete';
import {
    ScrollInfoContainer,
    UserLabel,
    UserValue,
    UserInfoColName,
    UserInfoColValue,
    Title,
    CardTitleRow,
} from './styles';
import { mapRoles } from '../../../../utils/roleMapper';

const IdVerificationContainer = styled.span`
    border-radius: 5px;
    padding: 2px 10px;
    max-height: 25px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ bgColor }) => bgColor || 'transparent'};
    color: ${({ textColor }) => textColor || '#44484C'};
`;

const StepInfoIcon = styled(FontAwesomeIcon)`
    color: #c1c1c1;
    cursor: help;
    margin-left: 6px;
    margin-right: 4px;
    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const UserInfoRows = ({ user, userLoading, onRefreshData }) => {
    const { t } = useTranslation();
    const [openConfirmIdModal, setOpenConfirmIdModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedUserForModal, setSelectedUserForModal] = useState({});
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [idvModalMode, setIdvModalMode] = useState('new');

    if (!user) {
        return null;
    }

    const attributes = {};
    const leftColumnOrder = [
        'fullName',
        'username',
        'email',
        'status',
        'roles',
        'groups',
        'country',
        'region',
        'personalEmail'
    ];

    attributes.fullName =
        [user.firstName, user.lastName].filter((name) => name).join(' ') || '';
    ['username', 'email', 'groups'].forEach((attr) => {
        attributes[attr] = user[attr] || '';
    });
    attributes.roles = mapRoles(user.roles) || '';

    if (user.status) {
        attributes.status = user.status;
    }

    const displayNameMap = {};

    (user.customAttributes || []).forEach((attr) => {
        let wasMapped = false;
        let mappedKey = null;
        if (
            attr.key === 'addresses[type eq "work"].country' ||
            attr.key === 'addresses[type eq "home"].country' ||
            attr.key === 'co'
        ) {
            attributes['country'] = attr.value;
            mappedKey = 'country';
            wasMapped = true;
        } else if (
            attr.key === 'addresses[type eq "work"].region' ||
            attr.key === 'addresses[type eq "home"].region' ||
            attr.key === 'st'
        ) {
            attributes['region'] = attr.value;
            mappedKey = 'region';
            wasMapped = true;
        } else if (
            attr.key === 'emails[type eq "work"].value' ||
            attr.key === 'emails[type eq "home"].value' ||
            attr.key === 'otherMailbox'
        ) {
            attributes['personalEmail'] = attr.value;
            mappedKey = 'personalEmail';
            wasMapped = true;
        }
        // Only store with original key if it wasn't mapped (prevents duplicates)
        if (!wasMapped) {
            mappedKey = attr.key;
            attributes[attr.key] = attr.value;
        }
        if (mappedKey && attr.displayName) {
            displayNameMap[mappedKey] = attr.displayName;
        }
    });

    const idvStatus = user.idVerificationStatus || 'Unconfirmed';
    const confirmationInitiatedBy = user.operator || '';
    const confirmationInitiatedAt = user.lastVerificationDate;
    const confirmationCompletedAt = user.lastUpdatedVerificationDate;

    const convertDate = (date) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        };
        return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
    };

    const generateTooltipContent = () => {
        const confirmationText = `${t(
            'usersTable$column$actions$tooltip$confirmationInitiated',
        )} ${confirmationInitiatedBy} ${t(
            'usersTable$column$actions$tooltip$on',
        )} ${
            confirmationInitiatedAt ? convertDate(confirmationInitiatedAt) : ''
        }`;

        const finishedText = `${t(
            'usersTable$column$actions$tooltip$userFinishedConfirmation',
        )} ${
            confirmationCompletedAt ? convertDate(confirmationCompletedAt) : ''
        }`;

        return { confirmationText, finishedText };
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return t('idv$timestamp$notAvailable');
        try {
            return new Date(timestamp).toLocaleString();
        } catch (e) {
            console.warn('Invalid timestamp format:', timestamp);
            return t('idv$timestamp$notAvailable');
        }
    };

    const showConfirmIdModal = async () => {
        if (!user?.username) return;
        try {
            const res = await fetch(`${apiPath}/user/${encodeURIComponent(user.username)}/verification/status`, {
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
            });
            if (res.ok) {
                const statusData = await res.json();
                setIdvModalMode(statusData?.hasActivePendingLink ? 'resend' : 'new');
            } else {
                setIdvModalMode('new');
            }
        } catch (e) {
            setIdvModalMode('new');
        }
        setOpenConfirmIdModal(true);
        setSelectedUserForModal({ ...user });
    };

    const hideConfirmIdModal = (modalData) => {
        setOpenConfirmIdModal(false);
        setSelectedUserForModal({});
    };

    const showDeleteModal = () => {
        if (user?.username) {
            setOpenDeleteModal(true);
            setSelectedUserForModal({ ...user });
        }
    };

    const hideDeleteModal = async (modalData) => {
        setOpenDeleteModal(false);
        setSelectedUserForModal({});
        if (modalData?.submit && onRefreshData) {
            setIsRefreshing(true);
            try {
                await onRefreshData();
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    // Step-level outcome fields (null for legacy records that pre-date this feature)
    const identityVerificationStepOutcome = user.identityVerificationStepOutcome || null;
    const passwordGenerationStepOutcome = user.passwordGenerationStepOutcome || null;
    const hasStepLevelData = identityVerificationStepOutcome !== null;
    const showGuidanceIcon = identityVerificationStepOutcome === 'SUCCESS' && passwordGenerationStepOutcome === 'FAILED';
    const showExpiredIcon = identityVerificationStepOutcome === 'EXPIRED';
    const showPasswordGenerationRow = user.tapGenerationConfigured || user.adPasswordGenerationConfigured;

    const canDeleteIdv = user.canDeleteId && (idvStatus === 'Confirmed' || idvStatus === 'Failed' || idvStatus === 'Expired');
    const isDeleteDisabled = idvStatus === 'Pending';
    const isUserDisabledOrDeleted = user.status === 'DISABLED' || user.status === 'DELETED';
    const canShowConfirmButton = user.canConfirmId;

    const renderStepBadge = (outcomeKey, fallbackLabel) => {
        const outcome = confirmStepOutcome[outcomeKey];
        if (!outcome && !fallbackLabel) return null;
        const label = outcome ? outcome.name : fallbackLabel;
        const bgColor = outcome ? outcome.color : '#F0F0F0';
        const textColor = outcome ? outcome.text_color : '#888888';
        return (
            <IdVerificationContainer bgColor={bgColor} textColor={textColor}>
                {label}
            </IdVerificationContainer>
        );
    };

    const renderRow = (attr) => {
        let value;
        if (attributes[attr] instanceof Array) {
            value = attributes[attr].join(', ');
        } else {
            value = attributes[attr];
        }

        // Don't render rows with empty values
        if (!value || value === '') {
            return null;
        }

        const label =
            displayNameMap[attr] ||
            t(`userInfo$attributes$${attr}`) ||
            attr.charAt(0).toUpperCase() + attr.substring(1);
        return (
            <React.Fragment key={label}>
                <UserLabel md={4}>{`${label}:`}</UserLabel>
                <UserValue>{value}</UserValue>
            </React.Fragment>
        );
    };

    if (userLoading || isRefreshing) return <LoadingSpinner />;
    return (
        <>
            <CardTemplate testId={userInfoCardId}>
                <ScrollInfoContainer data-test-id={userInfoRowContainerId}>
                    <CardTitleRow>
                        <Col>
                            <Title>{`${t('cardTitles$userInfo')}`}</Title>
                        </Col>
                    </CardTitleRow>
                    <Row>
                        <UserInfoColName>
                            {/* Standard attributes in predefined order */}
                            {leftColumnOrder
                                .filter(
                                    (attr) =>
                                        attributes.hasOwnProperty(attr),
                                )
                                .map((attr) => renderRow(attr))}

                            {/* Custom attributes in alphabetical order */}
                            {Object.keys(attributes)
                                .filter((key) => !leftColumnOrder.includes(key))
                                .filter((key) => attributes[key] && attributes[key] !== '')
                                .sort()
                                .map((attr) => renderRow(attr))}
                        </UserInfoColName>
                        <UserInfoColValue>
                            {(user.canViewIdVerificationStatus || user.canConfirmId || user.canDeleteId) && (
                                <>
                                    {/* Overall Outcome / IDV Status Section in Right Column */}
                                    <React.Fragment key="idv-status">
                                        <UserLabel>
                                            {t('idv$status$label')}
                                            {showGuidanceIcon && (
                                                <>
                                                    <StepInfoIcon
                                                        id="idv-guidance-icon"
                                                        icon={faInfoCircle}
                                                    />
                                                    <UncontrolledTooltip
                                                        placement="bottom"
                                                        target="idv-guidance-icon"
                                                    >
                                                        <div style={{ textAlign: 'left', padding: '2px' }}>
                                                            <strong style={{ display: 'block', marginBottom: '4px' }}>
                                                                {t('idv$step$guidance$title')}
                                                            </strong>
                                                            <ul style={{ margin: 0, paddingLeft: '14px' }}>
                                                                {user.tapGenerationConfigured && (
                                                                    <li>{t('idv$step$guidance$policy')}</li>
                                                                )}
                                                                {user.adPasswordGenerationConfigured && (
                                                                    <li>{t('idv$step$guidance$adConfig')}</li>
                                                                )}
                                                                <li>{t('idv$step$guidance$connectivity')}</li>
                                                            </ul>
                                                        </div>
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                            {showExpiredIcon && (
                                                <>
                                                    <StepInfoIcon
                                                        id="idv-expired-icon"
                                                        icon={faInfoCircle}
                                                    />
                                                    <UncontrolledTooltip
                                                        placement="bottom"
                                                        target="idv-expired-icon"
                                                    >
                                                        <div style={{ textAlign: 'left', padding: '2px' }}>
                                                            <strong style={{ display: 'block', marginBottom: '4px' }}>
                                                                {t('idv$step$guidance$expiredTitle')}
                                                            </strong>
                                                            <ul style={{ margin: 0, paddingLeft: '14px' }}>
                                                                <li>{t('idv$step$guidance$expiredResend')}</li>
                                                            </ul>
                                                        </div>
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                            {' :'}
                                        </UserLabel>
                                        <UserValue>
                                            <TableCellWrapper>
                                                {user.canViewIdVerificationStatus && (
                                                    <IdVerificationContainer
                                                        bgColor={
                                                            idConfirmationStatus[idvStatus]
                                                                ?.color
                                                        }
                                                        textColor={
                                                            idConfirmationStatus[idvStatus]
                                                                ?.text_color
                                                        }
                                                        id="idv-status-badge"
                                                        role="status"
                                                        aria-live="polite"
                                                    >
                                                        {idvStatus}
                                                    </IdVerificationContainer>
                                                )}
                                                {canShowConfirmButton && (
                                                    <>
                                                        <SmallAccentButton
                                                            outline
                                                            color="primary"
                                                            size="sm"
                                                            onClick={showConfirmIdModal}
                                                            disabled={isUserDisabledOrDeleted}
                                                            id="confirm-id-button"
                                                        >
                                                            {t('usersTable$column$actions$confirmIdButton')}
                                                        </SmallAccentButton>
                                                        {isUserDisabledOrDeleted && (
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target="confirm-id-button"
                                                            >
                                                                {user.status === 'DISABLED'
                                                                    ? t('idv$error$userDisabled')
                                                                    : t('idv$error$userDeleted')}
                                                            </UncontrolledTooltip>
                                                        )}
                                                    </>
                                                )}
                                                {canDeleteIdv && (
                                                    <SmallAccentButton
                                                        outline
                                                        color="danger"
                                                        size="sm"
                                                        onClick={showDeleteModal}
                                                        aria-label={t(
                                                            'idv$delete$button',
                                                        )}
                                                    >
                                                        {t('idv$delete$button')}
                                                    </SmallAccentButton>
                                                )}
                                            </TableCellWrapper>
                                        </UserValue>
                                    </React.Fragment>

                                    {/* Step-level outcomes — only shown when step data is available (not for legacy records) */}
                                    {hasStepLevelData && user.canViewIdVerificationStatus && (
                                        <>
                                            <React.Fragment key="idv-step-identity">
                                                <UserLabel style={{ whiteSpace: 'nowrap' }}>
                                                    {t('idv$step$identityVerification')}:
                                                </UserLabel>
                                                <UserValue>
                                                    {renderStepBadge(identityVerificationStepOutcome)}
                                                </UserValue>
                                            </React.Fragment>
                                            {showPasswordGenerationRow && (
                                                <React.Fragment key="idv-step-credential">
                                                    <UserLabel style={{ whiteSpace: 'nowrap' }}>
                                                        {t('idv$step$passwordGeneration')}:
                                                    </UserLabel>
                                                    <UserValue>
                                                        {passwordGenerationStepOutcome
                                                            ? renderStepBadge(passwordGenerationStepOutcome)
                                                            : renderStepBadge(null, t('idv$step$statusUnavailable'))}
                                                    </UserValue>
                                                </React.Fragment>
                                            )}
                                        </>
                                    )}

                                    {/* Show initiated by and timestamp for Pending, Confirmed, Failed, Expired */}
                                    {(idvStatus === 'Pending' ||
                                        idvStatus === 'Confirmed' ||
                                        idvStatus === 'Failed' ||
                                        idvStatus === 'Expired') && (
                                        <>
                                            {confirmationInitiatedBy && (
                                                <React.Fragment key="idv-initiated-by">
                                                    <UserLabel md={4} style={{ whiteSpace: 'nowrap' }}>
                                                        {t('idv$initiatedBy')}:
                                                    </UserLabel>
                                                    <UserValue>
                                                        {confirmationInitiatedBy}
                                                    </UserValue>
                                                </React.Fragment>
                                            )}
                                            <React.Fragment key="idv-initiated-at">
                                                <UserLabel md={4} style={{ whiteSpace: 'nowrap' }}>
                                                    {t('idv$timestamp$initiated')}:
                                                </UserLabel>
                                                <UserValue>
                                                    {formatTimestamp(
                                                        confirmationInitiatedAt,
                                                    )}
                                                </UserValue>
                                            </React.Fragment>
                                        </>
                                    )}

                                    {/* Show completed timestamp for Confirmed, Failed */}
                                    {(idvStatus === 'Confirmed' ||
                                        idvStatus === 'Failed') && (
                                        <React.Fragment key="idv-completed-at">
                                            <UserLabel md={4} style={{ whiteSpace: 'nowrap' }}>
                                                {t('idv$timestamp$completed')}:
                                            </UserLabel>
                                            <UserValue>
                                                {formatTimestamp(
                                                    confirmationCompletedAt,
                                                )}
                                            </UserValue>
                                        </React.Fragment>
                                    )}
                                </>
                            )}
                        </UserInfoColValue>
                    </Row>
                </ScrollInfoContainer>
            </CardTemplate>

            {openDeleteModal && (
                <UserIdVerificationDelete
                    onClose={hideDeleteModal}
                    data={selectedUserForModal}
                />
            )}

            {openConfirmIdModal && (
                <UsersIdConfirmation
                    onClose={hideConfirmIdModal}
                    data={selectedUserForModal}
                    mode={idvModalMode}
                />
            )}
        </>
    );
};

export default UserInfoRows;
